// The contract every model keeps, with nothing imported into it.
//
// Models import this, and `models/index.js` re-exports it alongside the model
// registry. Splitting it out that way keeps the registry from importing the
// models that import the registry — a cycle that only bites when someone loads
// one model module directly, which is exactly what a test does.
//
// @typedef {object} Model
// @property {string} id
// @property {number} version
// @property {string[]|null} sports      null means every sport
// @property {boolean} needsFit
// @property {(history, asOf, params?, opts?) => object} fit
// @property {(state, match, selection) => number|null} predict
// @property {object} defaultParams

/** Thrown by `assertBefore` when a strict fit is handed a match it must not see. */
export class LookaheadError extends Error {
  constructor(message) {
    super(message);
    this.name = 'LookaheadError';
  }
}

/**
 * History a model is allowed to learn from: everything that kicked off before
 * `asOf`. Strict mode refuses the whole call instead of quietly filtering,
 * which is what a backtest wants — a future match in the training set is a bug
 * in the harness, not a row to drop.
 *
 * Every `fit` calls this on the way in, so the defence sits at the model
 * boundary and no caller can forget it.
 */
export function assertBefore(history, asOf, strict = false) {
  const cutoff = new Date(asOf).getTime();
  if (!Number.isFinite(cutoff)) throw new TypeError(`asOf is not a date: ${asOf}`);
  const rows = Array.isArray(history) ? history : [];
  if (strict) {
    const ahead = rows.find((m) => new Date(m?.kickoff).getTime() >= cutoff);
    if (ahead) {
      throw new LookaheadError(`match ${ahead.matchKey ?? ahead.kickoff} kicks off at or after ${asOf}`);
    }
    return rows;
  }
  return rows.filter((m) => new Date(m?.kickoff).getTime() < cutoff);
}

/**
 * A fitted model in the shape `runStrategy` consumes through `ctx.models`:
 * `{ id, predict(match, selection) }`, with the state already closed over.
 */
export function bindModel(model, state) {
  return {
    id: model.id,
    predict: (match, selection) => model.predict(state, match, selection),
  };
}
